import { PublicPlannerEnum } from './../../../services/api-names';
import { HttpBackend, HttpClient, HttpHeaders } from '@angular/common/http';
import { Inject, Injectable, NgZone } from '@angular/core';
import { BehaviorSubject, Observable, Observer, throwError } from 'rxjs';
import { map, catchError } from 'rxjs/operators';
import { Serializer } from '../../../helpers/serializer/seriealizer';
import {
  TECH_LIB_PORTAL_CONFIG,
  TechLibPortalConfig,
} from '../../../tech-lib-portal.interceptor';
import { CompleteDataEnum } from '../../../services/api-names';
import { Utils } from '../../../utils/utils';
import { SessionService } from './../../../services/session.service';
import { ClientData } from '../request/models/client-data.model';
import { CompleteDataPortfolioDataInterface } from '../request/interfaces/portfolio-data.interface';
import { OrderSignedInterface } from '../../sign/request/interfaces/order-signed.interface';
import { mapOrderSigned } from '../../sign/services/order-signed.helpers';

@Injectable({
  providedIn: 'root',
})
export class CompletedataService {
  constructor(
    @Inject(TECH_LIB_PORTAL_CONFIG) private config: TechLibPortalConfig,
    private httpBackend: HttpBackend,
    private http: HttpClient
  ) {}

  public getEnumeration(
    enumerationType: string,
    parametre?: string
  ): Observable<any> {
    var url;
    parametre != undefined
      ? (url = Utils.formatString(
          this.config.baseApi + CompleteDataEnum.apirest_get_enumeration2,
          [enumerationType, parametre]
        ))
      : (url = Utils.formatString(
          this.config.baseApi + CompleteDataEnum.apirest_get_enumeration,
          [enumerationType]
        ));

    return new Observable((observer: Observer<any>) => {
      this.http
        .get<any>(url)
        .pipe(
          map((res) => {
            return res;
          }),
          catchError((err) => {
            return throwError(err);
          })
        )
        .subscribe(
          (resp: any) => {
            observer.next(resp);
            observer.complete();
          },
          (err) => observer.error(err)
        );
    });
  }

  public getFullClientData() {
    const url = this.config.baseApi + CompleteDataEnum.apirest_full_client_data;
    return this.http.get<any>(url).pipe(
      map((res) => {
        return res.NaturalClient;
      }),
      catchError((err) => {
        return throwError(err);
      })
    );
  }

  public setFullClientData(fullclientdata: ClientData) {
    const url = this.config.baseApi + CompleteDataEnum.apirest_full_client_data;
    const bodyRequest =
      '{"NaturalClient": ' + JSON.stringify(fullclientdata) + '}';

    return this.http.post<any>(url, bodyRequest).pipe(
      map((res) => {
        return res.NaturalClient;
      }),
      catchError((err) => {
        return throwError(err);
      })
    );
  }

  public getWhatLegalDocuments() {
    const url =
      this.config.baseApi + CompleteDataEnum.apirest_get_what_legaldocuments;
    return this.http.get<any>(url).pipe(
      map((res) => {
        return res;
      }),
      catchError((err) => {
        return throwError(err);
      })
    );
  }

  public uploadDocument(file: any, typeId: string) {
    const httpclientNoHeaders = new HttpClient(this.httpBackend);
    let headers: HttpHeaders = new HttpHeaders();
    //headers = headers.append('Content-Type', 'multipart/form-data');
    headers = headers.set('Tower30-SessionToken', this.config.sessionToken);
    headers = headers.set('Tower30-ClientCode', this.config.clientCode);
    headers = headers.set('Tower30-Domain', this.config.domain);
    headers = headers.set('Tower30-Language', this.config.language);
    headers = headers.set('Authorization', `Bearer ${this.config.accessToken}`);

    let options = { headers: headers };
    const url = this.config.baseApi + CompleteDataEnum.apirest_upload_document;

    const bodyRequest: any = new FormData();
    bodyRequest.append('TypeId', String(typeId));
    bodyRequest.append('File', file, file.name);
    return httpclientNoHeaders.post<any>(url, bodyRequest, options).pipe(
      map((res) => {
        return res;
      }),
      catchError((err) => {
        return throwError(err);
      })
    );
  }

  public allUploadDocuments(file: any, typeId: string) {
    const httpclientNoHeaders = new HttpClient(this.httpBackend);
    let headers: HttpHeaders = new HttpHeaders();
    //headers = headers.append('Content-Type', 'multipart/form-data');
    headers = headers.set('Tower30-SessionToken', this.config.sessionToken);
    headers = headers.set('Tower30-ClientCode', this.config.clientCode);
    headers = headers.set('Tower30-Domain', this.config.domain);
    headers = headers.set('Tower30-Language', this.config.language);
    headers = headers.set('Authorization', `Bearer ${this.config.accessToken}`);

    let options = { headers: headers };
    const url = this.config.baseApi + CompleteDataEnum.apirest_upload_document;

    const bodyRequest: any = new FormData();
    bodyRequest.append('TypeId', String(typeId));
    file.forEach((file) => {
      bodyRequest.append('File', file, file.name);
    });
    return httpclientNoHeaders.post<any>(url, bodyRequest, options).pipe(
      map((res) => {
        return res;
      }),
      catchError((err) => {
        return throwError(err);
      })
    );
  }

  public uploadDocumentProofAddress(file: any) {
    const httpclientNoHeaders = new HttpClient(this.httpBackend);
    let headers: HttpHeaders = new HttpHeaders();
    headers = headers.set('Tower30-SessionToken', this.config.sessionToken);
    headers = headers.set('Tower30-ClientCode', this.config.clientCode);
    headers = headers.set('Tower30-Domain', this.config.domain);
    headers = headers.set('Tower30-Language', this.config.language);
    headers = headers.set('Authorization', `Bearer ${this.config.accessToken}`);

    let options = { headers: headers };

    const url =
      this.config.baseApi +
      CompleteDataEnum.apirest_upload_document_proofAddress;

    const bodyRequest: any = new FormData();
    bodyRequest.append('File', file, file.name);
    return httpclientNoHeaders.post<any>(url, bodyRequest, options).pipe(
      map((res) => {
        return res;
      }),
      catchError((err) => {
        return throwError(err);
      })
    );
  }

  public setStep(step: string, portfolioCode: string) {
    const url =
      this.config.baseApi +
      Utils.formatString(CompleteDataEnum.apirest_set_step, [portfolioCode]);
    const bodyRequest = '{"step": "' + step + '"}';
    return this.http.post<any>(url, bodyRequest).pipe(
      map((res) => {
        return res;
      }),
      catchError((err) => {
        return throwError(err);
      })
    );
  }

  public getFinancialPlanRefData() {
    const url = this.config.baseApi + PublicPlannerEnum.apirest_eval_refdata;
    return this.http.get<any>(url).pipe(
      map((res) => {
        return res;
      }),
      catchError((err) => {
        return throwError(err);
      })
    );
  }

  public setIncomesAndWorths(data: any) {
    data.forEach((element, index, array) => {
      if (index == array.length - 1)
        element.Description = '> ' + element.Description.replace(',', ' ');
      element.Description =
        element.Description.replace(',', '-') +
        ' ' +
        this.config.currencySymbolBase;
    });
    return data;
  }

  public funding(
    cashAmount: string,
    currency: string,
    bankname: string,
    modelPortfolioCode: string,
    exoneration: string,
    portfolioCode: string,
    payeeDeliveryCode: string
  ) {
    let bodyRequest = {
      Amount: cashAmount,
      Currency: currency,
      FundTransfers: [],
      BankName: bankname,
      ModelPortfolioCode: modelPortfolioCode,
      Exoneration: exoneration,
      PayeeDeliveryCode: payeeDeliveryCode,
    };

    const url = Utils.formatString(
      this.config.baseApi + PublicPlannerEnum.apirest_funding,
      [portfolioCode]
    );
    return this.http.post<any>(url, bodyRequest).pipe(
      map((res) => {
        return res;
      }),
      catchError((err) => {
        return throwError(err);
      })
    );
  }

  public getFunding(portfolioCode: string) {
    const url = Utils.formatString(
      this.config.baseApi + PublicPlannerEnum.apirest_funding,
      [portfolioCode]
    );
    return this.http.get<any>(url).pipe(
      map((res) => {
        return res;
      }),
      catchError((err) => {
        return throwError(err);
      })
    );
  }

  public getCustodioDoc(custodian: string, id?: string) {
    let url = Utils.formatString(
      this.config.baseApi + CompleteDataEnum.apirest_get_custodio_doc,
      [custodian]
    );
    if (id !== undefined)
      url = Utils.formatString(
        this.config.baseApi + CompleteDataEnum.apirest_get_custodio_doc2,
        [custodian, id]
      );

    return this.http.get<any>(url).pipe(
      map((res) => {
        return res;
      }),
      catchError((err) => {
        return throwError(err);
      })
    );
  }

  public getContractDoc(portfoliocode: string) {
    let url = Utils.formatString(
      this.config.baseApi + CompleteDataEnum.apirest_get_contract_doc,
      [portfoliocode]
    );

    return this.http.get<any>(url).pipe(
      map((res) => {
        return res;
      }),
      catchError((err) => {
        return throwError(err);
      })
    );
  }

  public getExonerationDoc(portfoliocode: string) {
    let url = Utils.formatString(
      this.config.baseApi + CompleteDataEnum.apirest_get_exoneration_doc,
      [portfoliocode]
    );

    return this.http.get<any>(url).pipe(
      map((res) => {
        return res;
      }),
      catchError((err) => {
        return throwError(err);
      })
    );
  }

  public getPortfolioData(portfolioCode: string) {
    let url = Utils.formatString(
      this.config.baseApi + CompleteDataEnum.apirest_get_portfolio_data,
      [portfolioCode]
    );

    return this.http.get<CompleteDataPortfolioDataInterface>(url).pipe(
      map((res) => {
        return res;
      }),
      catchError((err) => {
        return throwError(err);
      })
    );
  }

  public getPendingTaskIb(portfolioCode: string): Observable<any> {
    const url = Utils.formatString(
      this.config.baseApi + CompleteDataEnum.apirest_get_pendingtask_ib,
      [portfolioCode]
    );
    return new Observable((observer: Observer<any>) => {
      this.http
        .get<any>(url)
        .pipe(
          map((res) => {
            return res;
          }),
          catchError((err) => {
            return throwError(err);
          })
        )
        .subscribe(
          (resp: any) => {
            observer.next(resp);
            observer.complete();
          },
          (err) => observer.error(err)
        );
    });
  }

  public getPortfolioSign(
    portfoliocode: string
  ): Observable<OrderSignedInterface> {
    let url = Utils.formatString(
      this.config.baseApi + CompleteDataEnum.apirest_get_portfolio_sign,
      [portfoliocode]
    );
    return this.http
      .get<OrderSignedInterface>(url)
      .pipe(map((resp: any) => mapOrderSigned(resp)));
  }

  public finalizeOnboarding(
    signature: string,
    portfolioCode: string
  ): Observable<any> {
    const url = Utils.formatString(
      this.config.baseApi + CompleteDataEnum.apirest_finalize_onboarding,
      [portfolioCode]
    );
    const bodyRequest = { Signature: signature };
    return new Observable((observer: Observer<any>) => {
      this.http
        .post<any>(url, bodyRequest)
        .pipe(
          map((res) => {
            return res;
          }),
          catchError((err) => {
            return throwError(err);
          })
        )
        .subscribe(
          (resp: any) => {
            observer.next(resp);
            observer.complete();
          },
          (err) => observer.error(err)
        );
    });
  }
}
